// Deliberately dependency-free. This decision is a small truth table that is easy to get subtly
// wrong, so it lives where it can be unit tested — WebSocketUtils pulls in DataAccessUtils, whose
// `jsoncrush` import the current jest transform cannot parse.

export interface IncomingNoteAction {
	/** Refetch the notes so every view of them is current. */
	refresh: boolean;
	/** Bump the unread counter behind the "N new messages" pill. */
	count: boolean;
}

export interface IncomingNoteContext {
	/**
	 * The notes module has swapped the conversation out for another view (its Media, Files & Links
	 * tab). The reader is still on the module, but the feed — and so its scroll position — is gone.
	 */
	isFeedHidden: boolean;
	/** The browser tab is in the foreground. */
	isTabActive: boolean;
	/** The reader is at the end of the feed, i.e. following the conversation as it happens. */
	isScrolledToBottom: boolean;
}

/**
 * What to do with a note that just arrived over the websocket.
 */
export const getIncomingNoteAction = ({
	isFeedHidden,
	isTabActive,
	isScrolledToBottom,
}: IncomingNoteContext): IncomingNoteAction => {
	// Refresh so media shared right now shows up in the view they ARE looking at, and count it too:
	// they have not seen the note, and there is no live scroll position to infer that from — the
	// feed is unmounted, so its last known position still reads "at the bottom".
	if (isFeedHidden) {
		return { refresh: true, count: true };
	}
	// Following the conversation at the end of the feed — pull the note straight in.
	if (isTabActive && isScrolledToBottom) {
		return { refresh: true, count: false };
	}
	// Scrolled up, or the browser tab is in the background: leave the feed where it is and let the
	// unread pill do the telling.
	return { refresh: false, count: true };
};
